// Thumbs-up / thumbs-down feedback row rendered under each AnaMoni
// assistant bubble (W1-B-18). Thumbs-down expands a small textarea so the
// reviewer can answer "¿Qué falló?"; on submit, both rating + note POST to
// /api/inpact/feedback. Success collapses the row to a "Gracias" pill.
//
// Raw prompts and planner responses are deliberately excluded.
import { useState } from "react";
import { submitFeedback } from "../lib/api";

export interface MessageFeedbackProps {
  messageId: string;
}

type Status = "idle" | "down-open" | "submitting-up" | "submitting-down" | "done";

export function MessageFeedback({ messageId }: MessageFeedbackProps) {
  const [status, setStatus] = useState<Status>("idle");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);

  const send = async (rating: "up" | "down", noteText?: string) => {
    setStatus(rating === "up" ? "submitting-up" : "submitting-down");
    setError(null);
    try {
      await submitFeedback({
        messageId,
        rating,
        note: noteText?.trim() || null,
      });
      setStatus("done");
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo guardar");
      setStatus(rating === "down" ? "down-open" : "idle");
    }
  };

  const submitting = status === "submitting-up" || status === "submitting-down";
  const showForm = status === "down-open" || status === "submitting-down";

  if (status === "done") {
    return (
      <div className="anamoni-feedback anamoni-feedback--done" role="status">
        Gracias
      </div>
    );
  }

  return (
    <div className="anamoni-feedback" aria-label="Feedback">
      <div className="anamoni-feedback__row">
        <button
          type="button"
          className="anamoni-feedback__btn"
          onClick={() => void send("up")}
          disabled={submitting}
          aria-label="Útil"
          title="Útil"
        >
          {"\u{1F44D}"}
        </button>
        <button
          type="button"
          className="anamoni-feedback__btn"
          onClick={() => setStatus("down-open")}
          disabled={submitting}
          aria-label="No útil"
          title="No útil"
        >
          {"\u{1F44E}"}
        </button>
      </div>
      {showForm && (
        <form
          className="anamoni-feedback__form"
          onSubmit={(e) => {
            e.preventDefault();
            void send("down", note);
          }}
        >
          <label htmlFor={`fb-note-${messageId}`} className="visually-hidden">
            ¿Qué falló?
          </label>
          <textarea
            id={`fb-note-${messageId}`}
            className="anamoni-feedback__textarea"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="¿Qué falló?"
            rows={2}
            maxLength={500}
          />
          <button
            type="submit"
            className="anamoni-feedback__submit"
            disabled={submitting}
          >
            Enviar
          </button>
        </form>
      )}
      {error && (
        <div className="anamoni-feedback__error" role="alert">
          {error}
        </div>
      )}
    </div>
  );
}
